import { useRef, useCallback } from 'react'
import { CONTENTMAP, PAGE_TYPE, SEARCH_SOURCE_MAP } from '../constants'

/**
 * Analytics tracking for Coveo search interactions.
 *
 * Wraps window.fireTagEvent / window._satellite calls and delegates interactive-product
 * and add-to-cart events to the Coveo commerce engine.
 *
 * @param {object} params
 * @param {object}  params.activeCore          - Coveo search or productListing controller
 * @param {object}  params.cart                - Coveo cart controller
 * @param {Array}   params.facets              - Mapped facet list (from mapCoveoFacets)
 * @param {Map}     params.selectedFilters     - Active filter map (from mapBreadcrumbsToFilterMap)
 * @param {number}  params.numFound            - Total product results
 * @param {object}  params.origParams          - { q, rows, start }
 * @param {object}  params.contents            - AEM content tab data (brands, articles, solutions)
 * @param {object}  params.effectiveContentData - Content data including solutionsAndServices
 * @param {Array}   params.docs                - Content search docs (used for totalArticles count)
 * @param {Map}     params.contentSelectedFilters - Active filter map for the S&S tab
 *
 * @returns {{ trackSearchTabsData, trackProductClick, trackAddToCart }}
 */
export function useSearchAnalytics({
  activeCore,
  cart,
  facets,
  selectedFilters,
  numFound,
  origParams,
  contents,
  effectiveContentData,
  docs,
  contentSelectedFilters,
}) {
  const skipNextSearchTabsTracking = useRef(false)
  const prevSelectedFilters = useRef(new Map())
  const prevSnsFilters = useRef(new Map())

  /**
   * Computes which facets were added or removed since the last tracking call.
   * @param {Map}   previousFilters
   * @param {Map}   currentFilters
   * @param {Array} [facetsOverride] - Facet list to use for label lookup; defaults to product facets.
   */
  const getSelectedFiltersDiff = (previousFilters, currentFilters, facetsOverride) => {
    const facetsToUse = facetsOverride || facets
    const prev = previousFilters instanceof Map ? previousFilters : new Map()
    const curr = currentFilters instanceof Map ? currentFilters : new Map()
    const added   = []
    const removed = []

    const getFacetDisplayName = (facetKey) => {
      const found = facetsToUse.find(
        (f) => (f.key ?? f.state?.field) === facetKey
      )
      return found?.label ?? found?.state?.displayName ?? facetKey
    }

    curr.forEach((value, key) => {
      if (!prev.has(key)) {
        const facetKey   = value?.key   || value?.group
        const facetValue = value?.label || value?.val
        if (!facetKey || !facetValue) return
        added.push({ facetType: getFacetDisplayName(facetKey), facetValue })
      }
    })

    prev.forEach((value, key) => {
      if (!curr.has(key)) {
        const facetKey   = value?.key   || value?.group
        const facetValue = value?.label || value?.val
        if (!facetKey || !facetValue) return
        removed.push({ facetType: getFacetDisplayName(facetKey), deselectedFacetValues: facetValue })
      }
    })

    return { added, removed }
  }

  /**
   * Fires the searchInfo analytics event for the given tab.
   */
  const fireSearchTabsTracking = ({ tabType: activeTab }) => {
    const { products: product, brands, articles, solutions, solutionsAndServices } = CONTENTMAP
    const urlParams = new URLSearchParams(window.location.search)
    const qsrc      = urlParams.get('qsrc') || ''
    const query     = origParams.q || urlParams.get('q') || ''
    const isWildCardSearch = query === '*:*'
    const isCategorySearch = !!urlParams.get('category') && isWildCardSearch
    const searchType = SEARCH_SOURCE_MAP[qsrc] || 'Typed In'

    const pageTypeName = window.location.pathname.includes('/partner/')
      ? PAGE_TYPE.partner
      : (isCategorySearch ? PAGE_TYPE.brand : PAGE_TYPE.search)

    let deviceType
    if (window.innerWidth <= 568) deviceType = 'Mobile'
    else if (window.innerWidth <= 1024) deviceType = 'Tablet'
    else deviceType = 'Desktop'

    let nextSearchInfo = {
      searchKeyword: isWildCardSearch ? undefined : query,
      searchType,
      pageType:       pageTypeName,
      searchTabType:  CONTENTMAP[activeTab]?.tab || CONTENTMAP.products.tab,
      resultCounts:   {},
      searchFilter:   {},
    }

    if ((!query || isWildCardSearch) && !isCategorySearch) return

    const searchTabs = Object.values(CONTENTMAP).map((c) => c.id)
    if (searchTabs.indexOf(activeTab) < 0) return

    switch (activeTab) {
      case product.id: {
        nextSearchInfo.resultCounts = { [product.counts]: numFound }
        const predefinedFilterValues = [...selectedFilters.values()].map(
          (filter) => filter.label || filter.val
        )
        const { added, removed } = getSelectedFiltersDiff(prevSelectedFilters.current, selectedFilters)
        nextSearchInfo.searchFilter = {
          predefinedFilterValues,
          deviceType,
          totalArticles: docs?.slice(0, 5)?.length
        }

        if (added.length === 1) {
          nextSearchInfo.searchFilter.facetType  = added[0].facetType
          nextSearchInfo.searchFilter.facetValue = added[0].facetValue
        }
        if (removed.length === 1) {
          nextSearchInfo.searchFilter.facetType             = removed[0].facetType
          nextSearchInfo.searchFilter.deselectedFacetValues = removed[0].deselectedFacetValues
        }
        // Multiple removals (e.g. "Clear all") — fire one event per removed facet
        if (removed.length > 1) {
          removed.forEach((removedFilter) => {
            const clearAllSearchInfo = {
              ...nextSearchInfo,
              searchFilter: {
                predefinedFilterValues,
                deviceType,
                facetType:             removedFilter.facetType,
                deselectedFacetValues: removedFilter.deselectedFacetValues,
              },
            }
            window.fireTagEvent && window.fireTagEvent('searchInfo', JSON.parse(JSON.stringify(clearAllSearchInfo)))
          })
          prevSelectedFilters.current = new Map(selectedFilters)
          return
        }
        break
      }

      case solutionsAndServices.id: {
        if (
          !effectiveContentData?.solutionsAndServices ||
          effectiveContentData?.solutionsAndServices?.isSearching
        ) return
        const snsFacets = effectiveContentData?.solutionsAndServices?.facets || []
        const currentSnsFilters = contentSelectedFilters instanceof Map ? contentSelectedFilters : new Map()
        const predefinedSnsFilterValues = [...currentSnsFilters.values()].map((f) => f.label || f.val)
        const { added: snsAdded, removed: snsRemoved } = getSelectedFiltersDiff(
          prevSnsFilters.current, currentSnsFilters, snsFacets
        )

        nextSearchInfo.resultCounts = {
          [solutionsAndServices.counts]: effectiveContentData?.solutionsAndServices?.numFound,
        }
        nextSearchInfo.searchFilter = {
          predefinedFilterValues: predefinedSnsFilterValues,
          deviceType
        }

        if (snsAdded.length === 1) {
          nextSearchInfo.searchFilter.facetType  = snsAdded[0].facetType
          nextSearchInfo.searchFilter.facetValue = snsAdded[0].facetValue
        }
        if (snsRemoved.length === 1) {
          nextSearchInfo.searchFilter.facetType             = snsRemoved[0].facetType
          nextSearchInfo.searchFilter.deselectedFacetValues = snsRemoved[0].deselectedFacetValues
        }
        // Multiple removals (e.g. "Clear all") — fire one event per removed facet
        if (snsRemoved.length > 1) {
          snsRemoved.forEach((removedFilter) => {
            const clearAllSearchInfo = {
              ...nextSearchInfo,
              searchFilter: {
                predefinedFilterValues: predefinedSnsFilterValues,
                deviceType,
                facetType:             removedFilter.facetType,
                deselectedFacetValues: removedFilter.deselectedFacetValues,
              },
            }
            window.fireTagEvent && window.fireTagEvent('searchInfo', JSON.parse(JSON.stringify(clearAllSearchInfo)))
          })
          prevSnsFilters.current = new Map(currentSnsFilters)
          return
        }

        prevSnsFilters.current = new Map(currentSnsFilters)
        break
      }

      case brands.id:
        nextSearchInfo.resultCounts = { [brands.counts]: contents?.brands?.total }
        delete nextSearchInfo.searchFilter
        break

      case articles.id:
        nextSearchInfo.resultCounts = { [articles.counts]: contents?.articles?.results }
        delete nextSearchInfo.searchFilter
        break

      case solutions.id:
        nextSearchInfo.resultCounts = { [solutions.counts]: contents?.solutions?.results }
        delete nextSearchInfo.searchFilter
        break

      default:
        break
    }

    const searchInfoSnapshot = JSON.parse(JSON.stringify(nextSearchInfo))
    window.fireTagEvent && window.fireTagEvent('searchInfo', searchInfoSnapshot)
    window._satellite && window._satellite.track && window._satellite.track('searchInfo')

    prevSelectedFilters.current = new Map(selectedFilters)
  }

  /**
   * Fires searchInfo tracking for a tab switch, skipping once if the flag is set
   * (used to suppress the event when a programmatic tab change should not be tracked).
   */
  const trackSearchTabsData = ({ tabType: activeTab }) => {
    if (skipNextSearchTabsTracking.current) {
      skipNextSearchTabsTracking.current = false
      return
    }
    fireSearchTabsTracking({ tabType: activeTab })
  }

  /** Notifies Coveo of an interactive product click (for click-through analytics). */
  const trackProductClick = useCallback((coveoProduct) => {
    if (!coveoProduct) return
    try {
      const interactive = activeCore.interactiveProduct({ options: { product: coveoProduct } })
      interactive.select()
    } catch (e) {
      // Non-critical — do not surface to users
    }
  }, [activeCore])

  /** Notifies Coveo of an add-to-cart event. */
  const trackAddToCart = useCallback(({ productId, name, price, quantity = 1 }) => {
    if (!productId) return
    try {
      cart.updateItemQuantity({ productId, name, price, quantity })
    } catch (e) {
      // Non-critical — do not surface to users
    }
  }, [cart])

  return { trackSearchTabsData, trackProductClick, trackAddToCart }
}
